/* eslint-disable react/prop-types */
import React, { ReactElement, useEffect, useMemo, useState } from "react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  useMantineReactTable,
} from "mantine-react-table";
import { formatAddress, formatAmountWithLabel } from "../../utils/formatter";
import { formatDate } from "../../utils/formatTime";
import styles from "./AddressDetailsTabs.module.scss";
import { Group, Text } from "@mantine/core";
import { FundsHistory, Status } from "../../models/FundsHistory";
import { useParams } from "react-router-dom";
import CopyTextComponent from "../copyText/CopyText.component";
import { useQuery } from "@tanstack/react-query";
import { QueryKey } from "../../models/enums";
import { IndexerService } from "../../services/indexer.service";
import TableFilterDropdown from "../tableFilterDropdown/TableFilterDropdown";
import { FundsHistoryResponse } from "../../models/Response";
import { useStore } from "@tanstack/react-store";
import { IStore, store } from "../../services/store.service";

const statusTypes = [
  { value: "all", label: "All types" },
  { value: Status.STAKED, label: "Staked" },
  { value: Status.UNSPENT, label: "Unspent" },
];
const initialPagination = {
  pageIndex: 0,
  pageSize: 10, //customize the default page size
};
export default function UnspentTab({
  active,
}: {
  active: boolean;
}): ReactElement {
  const timeZone = useStore<IStore, IStore["timeZone"]>(store, (state) => state.timeZone).selected;
  const { address } = useParams();
  const [selectedStatus, setSelectedStatus] = useState<Status | null>(null);
  const [pagination, setPagination] = useState(initialPagination);

  const { data: filteredFundsHistoryData } = useQuery({
    queryKey: [QueryKey.fundsHistory, address, selectedStatus, pagination],
    queryFn: async (): Promise<FundsHistoryResponse> => {
      const validStatuses = [Status.UNSPENT, Status.STAKED];
      return IndexerService.getAddressFundsHistory(
        address!,
        pagination.pageSize,
        pagination.pageIndex * pagination.pageSize,
        selectedStatus ? [selectedStatus] : validStatuses
      );
    },
    enabled: !!address && active,
    staleTime: 10000,
  });

  //should be memoized or stable
  const columns = useMemo<MRT_ColumnDef<FundsHistory>[]>(
    () => [
      {
        accessorKey: "TxID",
        header: "Id",
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
        enableSorting: false,
        Cell: ({ cell }) => (
          <CopyTextComponent
            primaryColor={false}
            text={formatAddress(cell.getValue<string>())}
            value={cell.getValue<string>()}
            link={`/tx/${cell.getValue<string>()}`}
            size={"14px"}
            weight={"500"}
          ></CopyTextComponent>
        ),
      },
      {
        accessorKey: "Status",
        header: "Type",
        Header: () => (
          <>
            <Text fw={500}>Type</Text>
            <TableFilterDropdown
              values={statusTypes}
              setSelectedValue={(item) => {
                setSelectedStatus(item ? (item as Status) : null);
              }}
              selectedValue={selectedStatus}
            />
          </>
        ),
        size: 150,
        enableSorting: false,
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
      },

      {
        accessorKey: "Amount", //normal accessorKey
        header: "Amount",
        mantineTableHeadCellProps: {
          align: "right",
        },
        mantineTableBodyCellProps: {
          align: "right",
        },
        enableSorting: false,
        Cell: ({ cell }) => (
          <>{formatAmountWithLabel(cell.getValue<number>(), true, 2, true)}</>
        ),
      },
      {
        accessorKey: "Timestamp",
        header: "Timestamp",
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
        size: 250,
        Cell: ({ cell, row }) => (
          <>
            {cell.getValue<string>() === null ? (
              <span> No date provided</span>
            ) : (
              <span>
                {
                  formatDate(new Date(row.original.Timestamp * 1000), timeZone)
                    .formattedDateTime
                }
                &nbsp;
                <span className={styles.unitLabel}>
                  {
                    formatDate(new Date(row.original.Timestamp * 1000), timeZone)
                      .utcOffset
                  }
                </span>
              </span>
            )}
          </>
        ),
      },
    ],
    [filteredFundsHistoryData?.data, timeZone]
  );

  const table = useMantineReactTable({
    columns: filteredFundsHistoryData ? columns : [],
    data: filteredFundsHistoryData?.data?.fundsHistory || [], //must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    mantineTableProps: {
      style: {
        tableLayout: "fixed",
        fontWeight: 500,
        fontSize: "var(--module-size-14)",
      },
    },
    mantineTableHeadCellProps: {
      style: {
        fontWeight: 500,
        fontSize: 15,
      },
    },
    state: {
      isLoading: !filteredFundsHistoryData,
      pagination,
    },
    mantinePaperProps: {
      withBorder: false,
      shadow: "none",
    },
    enableColumnActions: false,
    enableDensityToggle: false,
    manualPagination: true,
    rowCount: filteredFundsHistoryData?.data.count,
    onPaginationChange: setPagination,
    mantinePaginationProps: {
      showRowsPerPage: false,
      withEdges: false,
    },
    enableFullScreenToggle: false,
    enableHiding: false,
    enableFilters: false,
    initialState: {
      density: "xs",
    },
  });

  return (
    <>
      {!filteredFundsHistoryData ||
      (filteredFundsHistoryData.data?.count === 0 &&
        selectedStatus === null) ? (
        <Group h={100} align={"center"} justify={"center"}>
          <span>No unspent transaction outputs</span>
        </Group>
      ) : (
        <MantineReactTable table={table} />
      )}
    </>
  );
}
